import { useEffect, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { MultiSelect } from '../../../components/ui/MultiSelect.jsx';
import { FileInput } from '../../../components/ui/FileInput.jsx';
import { ragApi } from '../../../api/rag.js';
import { storesApi } from '../../../api/stores.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

const SOURCE_TYPES = [
  { value: 'product', label: 'Scheda prodotto' },
  { value: 'manual', label: 'Manuale' },
  { value: 'procedure', label: 'Procedura' },
  { value: 'article', label: 'Articolo' },
  { value: 'owner-content', label: 'Contenuto titolare' },
  { value: 'misc', label: 'Altro' }
];

const ACCEPT = '.pdf,.docx,.md,.markdown,.txt,.html,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown,text/html';

const EMPTY = {
  title: '',
  description: '',
  sourceType: 'misc',
  language: 'it',
  tags: [],
  storeIds: []
};

export function UploadDocumentModal({ open, onClose, reindexTarget }) {
  const queryClient = useQueryClient();
  const isReindex = Boolean(reindexTarget && reindexTarget._id);

  const storesQuery = useQuery({ queryKey: ['stores', 'all'], enabled: open, queryFn: () => storesApi.list({ limit: 100 }) });
  const storeOpts = useMemo(() => ((storesQuery.data && storesQuery.data.items) || []).map((s) => ({ value: s._id, label: s.name, hint: s.code })), [storesQuery.data]);

  const { register, handleSubmit, control, reset, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (isReindex) {
      reset({
        title: reindexTarget.title || '',
        description: reindexTarget.description || '',
        sourceType: reindexTarget.sourceType || 'misc',
        language: reindexTarget.language || 'it',
        tags: reindexTarget.tags || [],
        storeIds: (reindexTarget.storeIds || []).map((x) => x._id || x)
      });
    } else {
      reset(EMPTY);
    }
    setFile(null);
    setFileError(null);
  }, [open, reindexTarget, reset, isReindex]);

  const mutation = useMutation({
    mutationFn: (values) => {
      if (!file) {
        setFileError('File richiesto');
        throw new ApiError({ code: 'CLIENT_VALIDATION', message: 'File richiesto', statusCode: 400 });
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', values.title || file.name);
      if (values.description) fd.append('description', values.description);
      fd.append('sourceType', values.sourceType);
      fd.append('language', values.language || 'it');
      if (values.tags && values.tags.length) fd.append('tags', values.tags.join(','));
      if (values.storeIds && values.storeIds.length) fd.append('storeIds', values.storeIds.join(','));
      return isReindex
        ? ragApi.reindex(reindexTarget._id, fd)
        : ragApi.upload(fd);
    },
    onSuccess: () => {
      toast.success(isReindex ? 'Reindicizzazione avviata.' : 'Caricamento avviato.');
      queryClient.invalidateQueries({ queryKey: ['rag', 'documents'] });
      onClose();
    },
    onError: (err) => {
      if (err.code === 'CLIENT_VALIDATION') return;
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={isReindex ? `Reindicizza "${reindexTarget?.title}"` : 'Carica documento'}
      description={isReindex ? 'Il file caricato sostituirà i chunk esistenti.' : null}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="rag-upload" type="submit" loading={mutation.isPending}>
            {isReindex ? 'Reindicizza' : 'Carica'}
          </Button>
        </>
      }
    >
      <form id="rag-upload" className="flex flex-col gap-4" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <FileInput
          label="File"
          accept={ACCEPT}
          hint="PDF, DOCX, MD, HTML, TXT"
          value={file}
          onChange={(f) => { setFile(f); setFileError(null); }}
          error={fileError}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="Titolo" placeholder="Lasciare vuoto per usare il nome del file" error={errors.title?.message} {...register('title')} />
          <Select label="Tipo sorgente" options={SOURCE_TYPES} error={errors.sourceType?.message} {...register('sourceType')} />
          <Input label="Lingua" maxLength={8} {...register('language')} />
          <Controller
            control={control}
            name="tags"
            render={({ field }) => <TagInput label="Tag" value={field.value} onChange={field.onChange} lowercase placeholder="es. cibo, gatti" />}
          />
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="storeIds"
              render={({ field }) => (
                <MultiSelect
                  label="Negozi destinatari"
                  hint="Lasciare vuoto per renderlo disponibile a tutti i negozi."
                  options={storeOpts}
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Textarea label="Descrizione" rows={2} error={errors.description?.message} {...register('description')} />
          </div>
        </div>
      </form>
    </Modal>
  );
}
